import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateId } from '../../utils/geometry';
import { createEntityIdAllocator, formatEntityId, prefixForEquipment } from '../../utils/entityIds';
import {
  translatePolygon,
  isValidEquipmentFootprint,
  isDxfDerivedEquipment,
  equipmentReviewStatus,
  polygonBounds,
} from '../../utils/equipmentGeometry';
import { makeDefaultPorts, MIN_PORTS, nextPortIndex, updatePortInList } from '../../utils/ports';
import {
  assignInstanceIdentity,
  cloneEquipmentInstance,
  formatLoadPortId,
  nextInstanceSequence,
  normalizeSequencedInstances,
  parseSequencedName,
  shouldUseSequencedIdentity,
} from './equipmentNaming';
import {
  EQUIPMENT_LAYER_VERSION,
  defaultStyleForType,
  DEFAULT_FOUP_CAPACITY,
  DEFAULT_TOOL_FOUP_CAPACITY,
  DEFAULT_PROCESS_WAIT_SEC,
  DEFAULT_STOCKER_PROCESS_WAIT_SEC,
  categoryToProcessType,
  normalizeEquipmentProcessType,
} from './equipmentConstants';
import { mergeBuiltInEquipmentTemplates } from './builtInTemplates';
import type {
  EquipmentDrawMode,
  EquipmentInstance,
  EquipmentPoint,
  EquipmentPort,
  EquipmentProcessType,
  EquipmentSnapshot,
  EquipmentState,
  EquipmentTemplate,
} from './equipmentTypes';

const MAX_HISTORY = 60;

type EquipmentLoadPayload = {
  templates: EquipmentTemplate[] | Record<string, EquipmentTemplate>;
  instances: EquipmentInstance[] | Record<string, EquipmentInstance>;
  templateOrder?: string[];
  instanceOrder?: string[];
  visible?: boolean;
  showLabels?: boolean;
  showPorts?: boolean;
};

const seededBuiltInTemplates = mergeBuiltInEquipmentTemplates({}, []);

const initialState: EquipmentState = {
  templates: seededBuiltInTemplates.templates,
  templateOrder: seededBuiltInTemplates.templateOrder,
  instances: {},
  instanceOrder: [],
  visible: false,
  showLabels: false,
  showPorts: false,
  hiddenProcesses: {},
  selectedInstanceIds: [],
  selectedInstanceId: null,
  selectedTemplateId: null,
  portEditInstanceId: null,
  selectedPortId: null,
  layoutSaveMarkedIds: [],
  reviewSelectedIds: [],
  reviewDecisions: {},
  dxfOverlaysGenerated: false,
  draw: {
    active: false,
    mode: 'rectangle',
    points: [],
    pending: null,
    editingId: null,
  },
  history: { past: [], future: [] },
};

/** Fill Digital Twin defaults for instances loaded from older documents. */
function normalizeInstance(inst: EquipmentInstance): EquipmentInstance {
  const equipmentType: EquipmentProcessType = normalizeEquipmentProcessType(
    inst.equipmentType ?? categoryToProcessType(inst.category)
  );
  const isStocker = inst.objectType === 'stocker';
  const usesProcessWait = isStocker || inst.objectType === 'equipment';
  const defaultProcessWait = isStocker
    ? DEFAULT_STOCKER_PROCESS_WAIT_SEC
    : DEFAULT_PROCESS_WAIT_SEC;
  return {
    ...inst,
    equipmentType,
    reviewStatus: equipmentReviewStatus(inst),
    foupCapacity:
      typeof inst.foupCapacity === 'number' && Number.isFinite(inst.foupCapacity)
        ? inst.foupCapacity
        : isStocker
          ? DEFAULT_FOUP_CAPACITY
          : inst.objectType === 'equipment'
            ? DEFAULT_TOOL_FOUP_CAPACITY
            : inst.foupCapacity,
    outsideStocker: isStocker ? Boolean(inst.outsideStocker) : inst.outsideStocker,
    processWaitSec:
      typeof inst.processWaitSec === 'number' && Number.isFinite(inst.processWaitSec)
        ? inst.processWaitSec
        : usesProcessWait
          ? defaultProcessWait
          : inst.processWaitSec,
  };
}

/** Deep-ish snapshot of the persistent data (small, JSON-serialisable). */
function snapshot(state: EquipmentState): EquipmentSnapshot {
  return {
    templates: JSON.parse(JSON.stringify(state.templates)),
    templateOrder: [...state.templateOrder],
    instances: JSON.parse(JSON.stringify(state.instances)),
    instanceOrder: [...state.instanceOrder],
    reviewDecisions: { ...state.reviewDecisions },
  };
}

/**
 * Push the current persistent data onto the undo stack and clear redo. The entry
 * is stamped with the time of the edit that is about to be applied, which is
 * what lets a unified undo order equipment edits against layout edits.
 */
function pushHistory(state: EquipmentState) {
  state.history.past.push({ ...snapshot(state), timestamp: Date.now() });
  if (state.history.past.length > MAX_HISTORY) state.history.past.shift();
  state.history.future = [];
}

function nowIso() {
  return new Date().toISOString();
}

const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    // ── Overlay visibility ────────────────────────────────────────────────
    setEquipmentVisible: (state, action: PayloadAction<boolean>) => {
      state.visible = action.payload;
    },
    toggleEquipmentVisible: (state) => {
      state.visible = !state.visible;
    },
    setShowLabels: (state, action: PayloadAction<boolean>) => {
      state.showLabels = action.payload;
    },
    toggleShowLabels: (state) => {
      state.showLabels = !state.showLabels;
    },
    setShowPorts: (state, action: PayloadAction<boolean>) => {
      state.showPorts = action.payload;
    },
    toggleShowPorts: (state) => {
      state.showPorts = !state.showPorts;
    },

    // ── Process visibility filtering ───────────────────────────────────────
    toggleProcessVisible: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      if (state.hiddenProcesses[key]) delete state.hiddenProcesses[key];
      else state.hiddenProcesses[key] = true;
    },
    setProcessVisible: (state, action: PayloadAction<{ process: string; visible: boolean }>) => {
      const { process, visible } = action.payload;
      if (visible) delete state.hiddenProcesses[process];
      else state.hiddenProcesses[process] = true;
    },
    setAllProcessesVisible: (
      state,
      action: PayloadAction<{ processes: string[]; visible: boolean }>
    ) => {
      const { processes, visible } = action.payload;
      if (visible) {
        for (const p of processes) delete state.hiddenProcesses[p];
      } else {
        for (const p of processes) state.hiddenProcesses[p] = true;
      }
    },

    // ── Selection ─────────────────────────────────────────────────────────
    selectInstance: (state, action: PayloadAction<string | null>) => {
      state.selectedInstanceId = action.payload;
      state.selectedInstanceIds = action.payload ? [action.payload] : [];
      state.selectedPortId = null;
      if (action.payload) state.selectedTemplateId = null;
      if (state.portEditInstanceId && state.portEditInstanceId !== action.payload) {
        state.portEditInstanceId = null;
      }
    },
    selectInstances: (state, action: PayloadAction<string[]>) => {
      const ids = action.payload.filter(
        (id, index, all) => Boolean(state.instances[id]) && all.indexOf(id) === index
      );
      state.selectedInstanceIds = ids;
      state.selectedInstanceId = ids[0] ?? null;
      state.selectedPortId = null;
      if (ids.length > 0) state.selectedTemplateId = null;
      if (state.portEditInstanceId && !ids.includes(state.portEditInstanceId)) {
        state.portEditInstanceId = null;
      }
    },
    selectTemplate: (state, action: PayloadAction<string | null>) => {
      state.selectedTemplateId = action.payload;
    },
    setPortEditInstance: (state, action: PayloadAction<string | null>) => {
      state.portEditInstanceId = action.payload;
    },
    /** Select a single load port for the Properties panel (clears on null). */
    selectEquipmentPort: (
      state,
      action: PayloadAction<{ instanceId: string; portId: string } | null>
    ) => {
      if (!action.payload) {
        state.selectedPortId = null;
        return;
      }
      state.selectedInstanceId = action.payload.instanceId;
      state.selectedInstanceIds = [action.payload.instanceId];
      state.selectedPortId = action.payload.portId;
      state.selectedTemplateId = null;
    },
    toggleLayoutSaveMark: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const idx = state.layoutSaveMarkedIds.indexOf(id);
      if (idx >= 0) state.layoutSaveMarkedIds.splice(idx, 1);
      else state.layoutSaveMarkedIds.push(id);
    },
    addLayoutSaveMark: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!state.layoutSaveMarkedIds.includes(id)) {
        state.layoutSaveMarkedIds.push(id);
      }
    },
    /** Merge many IDs into the save list (does not remove existing marks). */
    addLayoutSaveMarks: (state, action: PayloadAction<string[]>) => {
      for (const id of action.payload) {
        if (id && !state.layoutSaveMarkedIds.includes(id)) {
          state.layoutSaveMarkedIds.push(id);
        }
      }
    },
    setLayoutSaveMarks: (state, action: PayloadAction<string[]>) => {
      state.layoutSaveMarkedIds = action.payload.filter(
        (id, index, arr) => Boolean(id) && arr.indexOf(id) === index
      );
    },
    clearLayoutSaveMarks: (state) => {
      state.layoutSaveMarkedIds = [];
    },

    // ── Potential-equipment review ────────────────────────────────────────
    setEquipmentReviewSelection: (state, action: PayloadAction<string[]>) => {
      state.reviewSelectedIds = action.payload.filter((id, index, ids) => {
        const inst = state.instances[id];
        return (
          ids.indexOf(id) === index &&
          Boolean(inst) &&
          isDxfDerivedEquipment(inst) &&
          equipmentReviewStatus(inst) === 'potential'
        );
      });
    },
    toggleEquipmentReviewSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const inst = state.instances[id];
      if (!inst || !isDxfDerivedEquipment(inst) || equipmentReviewStatus(inst) !== 'potential') {
        return;
      }
      const index = state.reviewSelectedIds.indexOf(id);
      if (index >= 0) state.reviewSelectedIds.splice(index, 1);
      else state.reviewSelectedIds.push(id);
    },
    setSelectedEquipmentReviewStatus: (state, action: PayloadAction<'accepted' | 'rejected'>) => {
      const selected = new Set(state.reviewSelectedIds);
      if (selected.size === 0) return;
      pushHistory(state);
      for (const id of selected) {
        const inst = state.instances[id];
        if (!inst || !isDxfDerivedEquipment(inst) || equipmentReviewStatus(inst) !== 'potential') {
          continue;
        }
        inst.reviewStatus = action.payload;
        inst.updatedAt = nowIso();
        const reviewKey = inst.metadata?.reviewKey;
        if (typeof reviewKey === 'string') {
          state.reviewDecisions[reviewKey] = action.payload;
        }
        if (action.payload === 'rejected') {
          state.layoutSaveMarkedIds = state.layoutSaveMarkedIds.filter(
            (markedId) => markedId !== id
          );
          state.selectedInstanceIds = state.selectedInstanceIds.filter(
            (selectedId) => selectedId !== id
          );
          if (state.selectedInstanceId === id) state.selectedInstanceId = null;
        }
      }
      if (
        state.selectedInstanceId === null ||
        !state.selectedInstanceIds.includes(state.selectedInstanceId)
      ) {
        state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      }
      state.reviewSelectedIds = [];
    },
    resetDxfEquipmentReview: (state) => {
      const dxfInstances = Object.values(state.instances).filter(isDxfDerivedEquipment);
      if (dxfInstances.length === 0) return;
      pushHistory(state);
      for (const inst of dxfInstances) inst.reviewStatus = 'potential';
      state.reviewSelectedIds = [];
      state.reviewDecisions = {};
      state.layoutSaveMarkedIds = state.layoutSaveMarkedIds.filter((id) => {
        const inst = state.instances[id];
        return !inst || !isDxfDerivedEquipment(inst);
      });
    },

    // ── Drawing tool ──────────────────────────────────────────────────────
    setDrawActive: (state, action: PayloadAction<boolean>) => {
      state.draw.active = action.payload;
      state.draw.points = [];
      if (action.payload) {
        // Identify Equipment reveals overlays so footprints can be edited.
        state.visible = true;
      } else {
        state.draw.pending = null;
        state.draw.editingId = null;
      }
    },
    setDrawMode: (state, action: PayloadAction<EquipmentDrawMode>) => {
      state.draw.mode = action.payload;
      state.draw.points = [];
    },
    addDrawPoint: (state, action: PayloadAction<EquipmentPoint>) => {
      state.draw.points.push(action.payload);
    },
    popDrawPoint: (state) => {
      state.draw.points.pop();
    },
    clearDrawPoints: (state) => {
      state.draw.points = [];
    },
    /** Commit a finished footprint and open the identify modal. */
    setPendingPolygon: (
      state,
      action: PayloadAction<{ polygon: EquipmentPoint[]; rotation?: number }>
    ) => {
      const polygon = action.payload.polygon;
      if (!isValidEquipmentFootprint(polygon)) return;
      state.draw.pending = {
        polygon,
        rotation: action.payload.rotation ?? 0,
      };
      state.draw.points = [];
    },
    clearPending: (state) => {
      state.draw.pending = null;
      state.draw.editingId = null;
    },
    /** Open the identify modal to edit an existing instance's semantics. */
    beginEditInstance: (state, action: PayloadAction<string>) => {
      const inst = state.instances[action.payload];
      if (!inst) return;
      state.draw.editingId = action.payload;
      state.draw.pending = { polygon: inst.polygon, rotation: inst.rotation };
    },

    // ── Instances ─────────────────────────────────────────────────────────
    addInstance: (state, action: PayloadAction<EquipmentInstance>) => {
      pushHistory(state);
      let inst = normalizeInstance(action.payload);
      const templatePorts =
        inst.templateId && state.templates[inst.templateId]
          ? state.templates[inst.templateId].ports
          : [];

      if (shouldUseSequencedIdentity(inst.objectType)) {
        const parsed = parseSequencedName(inst.name);
        const ports = inst.overrides.ports ?? [];
        const idPrefix = prefixForEquipment(inst.objectType);
        const candidateId =
          parsed && parsed.prefix === idPrefix ? formatEntityId(idPrefix, parsed.sequence) : null;
        const portsMatchTyped =
          Boolean(candidateId) &&
          ports.length > 0 &&
          ports.every((port, index) => port.id === formatLoadPortId(candidateId!, index + 1));
        // Honor a pre-allocated typed id from makeInstance (shared ST with layout).
        const preallocated =
          /^[A-Z]+-\d+$/.test(inst.id) &&
          inst.id.startsWith(`${idPrefix}-`) &&
          !state.instances[inst.id]
            ? Number(inst.id.slice(idPrefix.length + 1))
            : null;
        const sequence =
          portsMatchTyped && parsed
            ? parsed.sequence
            : Number.isFinite(preallocated) && (preallocated as number) > 0
              ? (preallocated as number)
              : nextInstanceSequence(state.instances, inst.objectType);
        inst = assignInstanceIdentity(inst, sequence, templatePorts);
      } else {
        const alloc = createEntityIdAllocator(Object.keys(state.instances));
        if (!/^[A-Z]+-\d+$/.test(inst.id)) {
          inst = { ...inst, id: alloc.nextForEquipment(inst.objectType) };
        }
        const effectivePorts = inst.overrides.ports ?? templatePorts;
        if (effectivePorts.length === 0) {
          const bounds = polygonBounds(inst.polygon ?? []);
          inst.overrides.ports = makeDefaultPorts({
            parentId: inst.id,
            parentType: inst.objectType === 'stocker' ? 'stocker' : 'equipment',
            width: Math.max(0, bounds.maxX - bounds.minX),
            height: Math.max(0, bounds.maxY - bounds.minY),
          });
        } else {
          inst.overrides.ports = effectivePorts.map((p, i) => ({
            ...p,
            id: formatLoadPortId(inst.id, i + 1),
            parentId: inst.id,
          }));
        }
      }

      state.instances[inst.id] = inst;
      state.instanceOrder.push(inst.id);
      // Leave selection alone — identify-draw and other creates should not
      // auto-arm resize/properties; callers that need selection (e.g. paste)
      // dispatch selectInstance themselves.
      state.draw.pending = null;
      state.draw.editingId = null;
      // Custom identify-draw must appear immediately (not only after View → Overlays).
      state.visible = true;
    },
    updateInstance: (
      state,
      action: PayloadAction<{
        id: string;
        changes: Partial<EquipmentInstance>;
        skipHistory?: boolean;
      }>
    ) => {
      const inst = state.instances[action.payload.id];
      if (!inst) return;
      if (!action.payload.skipHistory) pushHistory(state);
      state.instances[action.payload.id] = {
        ...inst,
        ...action.payload.changes,
        updatedAt: nowIso(),
      };
      state.draw.pending = null;
      state.draw.editingId = null;
    },
    /** Lightweight geometry update during a drag (no history spam). */
    moveInstance: (state, action: PayloadAction<{ id: string; dx: number; dy: number }>) => {
      const inst = state.instances[action.payload.id];
      if (!inst) return;
      inst.polygon = translatePolygon(inst.polygon, action.payload.dx, action.payload.dy);
    },
    /**
     * Shift many instances by the same delta, without touching history. Group
     * drags stream these every frame and snapshot once up front instead.
     */
    translateInstances: (
      state,
      action: PayloadAction<{ ids: string[]; dx: number; dy: number }>
    ) => {
      const { ids, dx, dy } = action.payload;
      const stamp = nowIso();
      for (const id of ids) {
        const inst = state.instances[id];
        if (!inst) continue;
        inst.polygon = translatePolygon(inst.polygon, dx, dy);
        inst.updatedAt = stamp;
      }
    },
    /**
     * Snapshot the current instances onto the undo stack. Lets a streamed edit
     * (a group drag) register its pre-edit state as the single undo target.
     */
    recordEquipmentHistory: (state) => {
      pushHistory(state);
    },
    setInstancePolygon: (
      state,
      action: PayloadAction<{
        id: string;
        polygon: EquipmentPoint[];
        rotation?: number;
        commit?: boolean;
      }>
    ) => {
      const inst = state.instances[action.payload.id];
      if (!inst) return;
      if (action.payload.commit) pushHistory(state);
      inst.polygon = action.payload.polygon;
      if (typeof action.payload.rotation === 'number') inst.rotation = action.payload.rotation;
      inst.updatedAt = nowIso();
    },
    removeInstance: (state, action: PayloadAction<string>) => {
      if (!state.instances[action.payload]) return;
      pushHistory(state);
      delete state.instances[action.payload];
      state.instanceOrder = state.instanceOrder.filter((id) => id !== action.payload);
      state.layoutSaveMarkedIds = state.layoutSaveMarkedIds.filter((id) => id !== action.payload);
      state.reviewSelectedIds = state.reviewSelectedIds.filter((id) => id !== action.payload);
      state.selectedInstanceIds = state.selectedInstanceIds.filter((id) => id !== action.payload);
      if (state.selectedInstanceId === action.payload) state.selectedInstanceId = null;
      if (!state.selectedInstanceId) {
        state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      }
      if (state.portEditInstanceId === action.payload) state.portEditInstanceId = null;
      state.selectedPortId = null;
    },
    removeInstances: (state, action: PayloadAction<string[]>) => {
      const ids = new Set(action.payload.filter((id) => Boolean(state.instances[id])));
      if (ids.size === 0) return;
      pushHistory(state);
      for (const id of ids) delete state.instances[id];
      state.instanceOrder = state.instanceOrder.filter((id) => !ids.has(id));
      state.layoutSaveMarkedIds = state.layoutSaveMarkedIds.filter((id) => !ids.has(id));
      state.reviewSelectedIds = state.reviewSelectedIds.filter((id) => !ids.has(id));
      state.selectedInstanceIds = state.selectedInstanceIds.filter((id) => !ids.has(id));
      state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      if (state.portEditInstanceId && ids.has(state.portEditInstanceId)) {
        state.portEditInstanceId = null;
      }
      state.selectedPortId = null;
    },
    duplicateInstance: (state, action: PayloadAction<string>) => {
      const src = state.instances[action.payload];
      if (!src) return;
      pushHistory(state);
      const templatePorts =
        src.templateId && state.templates[src.templateId]
          ? state.templates[src.templateId].ports
          : [];
      const copy = cloneEquipmentInstance(src, state.instances, templatePorts, {
        polygon: translatePolygon(src.polygon, 12, 12),
        createdAt: nowIso(),
        updatedAt: nowIso(),
      });
      state.instances[copy.id] = copy;
      state.instanceOrder.push(copy.id);
      state.selectedInstanceId = copy.id;
      state.selectedInstanceIds = [copy.id];
    },

    // ── Ports (per instance) ──────────────────────────────────────────────
    addPort: (state, action: PayloadAction<{ instanceId: string; port: EquipmentPort }>) => {
      const inst = state.instances[action.payload.instanceId];
      if (!inst) return;
      pushHistory(state);
      const current = inst.overrides.ports
        ? inst.overrides.ports
        : inst.templateId && state.templates[inst.templateId]
          ? JSON.parse(JSON.stringify(state.templates[inst.templateId].ports))
          : [];
      const nextIndex = nextPortIndex(current);
      const port = shouldUseSequencedIdentity(inst.objectType)
        ? {
            ...action.payload.port,
            id: formatLoadPortId(inst.id, nextIndex),
            name: `LP-${nextIndex}`,
            parentId: action.payload.instanceId,
            parentType:
              inst.objectType === 'stocker' ? ('stocker' as const) : ('equipment' as const),
          }
        : action.payload.port;
      inst.overrides.ports = [...current, port];
      inst.updatedAt = nowIso();
    },
    updatePort: (
      state,
      action: PayloadAction<{
        instanceId: string;
        portId: string;
        changes: Partial<EquipmentPort>;
        skipHistory?: boolean;
      }>
    ) => {
      const inst = state.instances[action.payload.instanceId];
      if (!inst) return;
      const base = inst.overrides.ports
        ? inst.overrides.ports
        : inst.templateId && state.templates[inst.templateId]
          ? JSON.parse(JSON.stringify(state.templates[inst.templateId].ports))
          : [];
      const idx = base.findIndex((p: EquipmentPort) => p.id === action.payload.portId);
      if (idx < 0) return;
      if (!action.payload.skipHistory) pushHistory(state);
      inst.overrides.ports = updatePortInList(base, action.payload.portId, action.payload.changes);
      inst.updatedAt = nowIso();
    },
    removePort: (state, action: PayloadAction<{ instanceId: string; portId: string }>) => {
      const inst = state.instances[action.payload.instanceId];
      if (!inst) return;
      const base = inst.overrides.ports
        ? inst.overrides.ports
        : inst.templateId && state.templates[inst.templateId]
          ? JSON.parse(JSON.stringify(state.templates[inst.templateId].ports))
          : [];
      // Never delete the final remaining port.
      if (base.length <= MIN_PORTS) return;
      pushHistory(state);
      inst.overrides.ports = base.filter((p: EquipmentPort) => p.id !== action.payload.portId);
      inst.updatedAt = nowIso();
      if (state.selectedPortId === action.payload.portId) state.selectedPortId = null;
    },

    // ── Templates ─────────────────────────────────────────────────────────
    addTemplate: (state, action: PayloadAction<EquipmentTemplate>) => {
      pushHistory(state);
      const t = action.payload;
      state.templates[t.id] = t;
      state.templateOrder.push(t.id);
      state.selectedTemplateId = t.id;
    },
    updateTemplate: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<EquipmentTemplate> }>
    ) => {
      const t = state.templates[action.payload.id];
      if (!t) return;
      pushHistory(state);
      state.templates[action.payload.id] = {
        ...t,
        ...action.payload.changes,
        updatedAt: nowIso(),
      };
    },
    renameTemplate: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const t = state.templates[action.payload.id];
      if (!t) return;
      pushHistory(state);
      t.name = action.payload.name;
      t.updatedAt = nowIso();
    },
    duplicateTemplate: (state, action: PayloadAction<string>) => {
      const src = state.templates[action.payload];
      if (!src) return;
      pushHistory(state);
      const id = generateId();
      const copy: EquipmentTemplate = {
        ...JSON.parse(JSON.stringify(src)),
        id,
        name: `${src.name} copy`,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      };
      state.templates[id] = copy;
      state.templateOrder.push(id);
      state.selectedTemplateId = id;
    },
    removeTemplate: (state, action: PayloadAction<string>) => {
      if (!state.templates[action.payload]) return;
      pushHistory(state);
      delete state.templates[action.payload];
      state.templateOrder = state.templateOrder.filter((id) => id !== action.payload);
      if (state.selectedTemplateId === action.payload) state.selectedTemplateId = null;
      // Detach instances that referenced this template; keep their resolved data
      // as overrides is out of scope here — simply drop the reference.
      for (const inst of Object.values(state.instances)) {
        if (inst.templateId === action.payload) inst.templateId = null;
      }
    },

    // ── Bulk load / clear (persistence) ───────────────────────────────────
    loadEquipment: (state, action: PayloadAction<EquipmentLoadPayload>) => {
      state.templates = {};
      state.templateOrder = [];
      state.instances = {};
      state.instanceOrder = [];
      state.reviewDecisions = {};
      const templates = Array.isArray(action.payload.templates)
        ? action.payload.templates
        : Object.values(action.payload.templates);
      const instances = Array.isArray(action.payload.instances)
        ? action.payload.instances
        : Object.values(action.payload.instances);
      const templateOrder = action.payload.templateOrder ?? templates.map((t) => t.id);
      const instanceOrder = action.payload.instanceOrder ?? instances.map((i) => i.id);

      for (const t of templates) {
        state.templates[t.id] = t;
      }
      state.templateOrder = templateOrder.filter((id) => Boolean(state.templates[id]));
      for (const i of instances) {
        const normalizedInstance = normalizeInstance(i);
        state.instances[i.id] = normalizedInstance;
        const reviewKey = normalizedInstance.metadata?.reviewKey;
        const reviewStatus = equipmentReviewStatus(normalizedInstance);
        if (
          typeof reviewKey === 'string' &&
          (reviewStatus === 'accepted' || reviewStatus === 'rejected')
        ) {
          state.reviewDecisions[reviewKey] = reviewStatus;
        }
      }
      state.instanceOrder = instanceOrder.filter((id) => Boolean(state.instances[id]));
      const normalized = normalizeSequencedInstances(
        state.instances,
        state.instanceOrder,
        state.templates
      );
      state.instances = normalized.instances;
      state.instanceOrder = normalized.instanceOrder;
      const merged = mergeBuiltInEquipmentTemplates(state.templates, state.templateOrder);
      state.templates = merged.templates;
      state.templateOrder = merged.templateOrder;
      state.visible = action.payload.visible ?? true;
      if (action.payload.showLabels !== undefined) state.showLabels = action.payload.showLabels;
      if (action.payload.showPorts !== undefined) state.showPorts = action.payload.showPorts;
      state.hiddenProcesses = {};
      state.selectedInstanceIds = [];
      state.selectedInstanceId = null;
      state.selectedTemplateId = null;
      state.portEditInstanceId = null;
      state.selectedPortId = null;
      state.layoutSaveMarkedIds = [];
      state.reviewSelectedIds = [];
      state.dxfOverlaysGenerated = Object.values(state.instances).some(isDxfDerivedEquipment);
      state.history = { past: [], future: [] };
    },
    clearEquipment: (state) => {
      pushHistory(state);
      state.templates = {};
      state.templateOrder = [];
      state.instances = {};
      state.instanceOrder = [];
      state.selectedInstanceIds = [];
      state.selectedInstanceId = null;
      state.selectedTemplateId = null;
      state.portEditInstanceId = null;
      state.selectedPortId = null;
      state.layoutSaveMarkedIds = [];
      state.reviewSelectedIds = [];
      state.reviewDecisions = {};
      state.dxfOverlaysGenerated = false;
    },

    // ── DXF-generated overlays ────────────────────────────────────────────
    /**
     * Replace every DXF-derived overlay with a freshly generated set, leaving
     * hand-drawn equipment untouched. Hidden DXF layers are filtered out before
     * this point, so anything they contributed is genuinely gone from the store
     * and cannot reach a saved layout or the simulation payload.
     */
    setDxfEquipmentOverlays: (
      state,
      action: PayloadAction<{
        templates: Record<string, EquipmentTemplate>;
        instances: Record<string, EquipmentInstance>;
        templateOrder: string[];
        instanceOrder: string[];
      }>
    ) => {
      const previousReviewByKey = new Map<string, EquipmentInstance['reviewStatus']>();
      for (const inst of Object.values(state.instances)) {
        const reviewKey = inst.metadata?.reviewKey;
        if (!isDxfDerivedEquipment(inst) || typeof reviewKey !== 'string') continue;
        const status = equipmentReviewStatus(inst);
        previousReviewByKey.set(reviewKey, status);
        if (status === 'accepted' || status === 'rejected') {
          state.reviewDecisions[reviewKey] = status;
        }
      }

      const manualInstanceIds = state.instanceOrder.filter(
        (id) => state.instances[id] && !isDxfDerivedEquipment(state.instances[id])
      );
      const manualInstances: Record<string, EquipmentInstance> = {};
      for (const id of manualInstanceIds) manualInstances[id] = state.instances[id];

      // Templates created by extraction are rebuilt from scratch; keep any the
      // user authored so the library does not lose hand-made entries.
      const manualTemplateIds = state.templateOrder.filter(
        (id) => state.templates[id]?.metadata?.source !== 'auto-extraction'
      );
      const manualTemplates: Record<string, EquipmentTemplate> = {};
      for (const id of manualTemplateIds) manualTemplates[id] = state.templates[id];

      state.templates = { ...manualTemplates };
      state.templateOrder = [...manualTemplateIds];
      for (const id of action.payload.templateOrder) {
        const template = action.payload.templates[id];
        if (!template) continue;
        if (!state.templates[id]) state.templateOrder.push(id);
        state.templates[id] = template;
      }

      state.instances = { ...manualInstances };
      state.instanceOrder = [...manualInstanceIds];
      const manualIdSet = new Set(manualInstanceIds);
      for (const id of action.payload.instanceOrder) {
        const instance = action.payload.instances[id];
        // Never let a generated overlay overwrite hand-drawn equipment.
        if (!instance || manualIdSet.has(id)) continue;
        if (!state.instances[id]) state.instanceOrder.push(id);
        const reviewKey = instance.metadata?.reviewKey;
        const previousStatus =
          typeof reviewKey === 'string'
            ? (state.reviewDecisions[reviewKey] ?? previousReviewByKey.get(reviewKey))
            : undefined;
        state.instances[id] = normalizeInstance({
          ...instance,
          reviewStatus: previousStatus ?? instance.reviewStatus,
        });
      }

      if (state.selectedInstanceId && !state.instances[state.selectedInstanceId]) {
        state.selectedInstanceId = null;
      }
      state.selectedInstanceIds = state.selectedInstanceIds.filter((id) =>
        Boolean(state.instances[id])
      );
      if (!state.selectedInstanceId) {
        state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      }
      if (state.portEditInstanceId && !state.instances[state.portEditInstanceId]) {
        state.portEditInstanceId = null;
        state.selectedPortId = null;
      }
      state.layoutSaveMarkedIds = state.layoutSaveMarkedIds.filter((id) =>
        Boolean(state.instances[id])
      );
      state.reviewSelectedIds = state.reviewSelectedIds.filter((id) => {
        const inst = state.instances[id];
        return Boolean(inst) && equipmentReviewStatus(inst) === 'potential';
      });
      state.dxfOverlaysGenerated = true;
      state.visible = true;
    },
    /** Drop every DXF-derived overlay, keeping hand-drawn equipment. */
    clearDxfEquipmentOverlays: (
      state,
      action: PayloadAction<{ clearReviewDecisions?: boolean; keepGenerated?: boolean } | undefined>
    ) => {
      for (const id of [...state.instanceOrder]) {
        const inst = state.instances[id];
        if (!inst || !isDxfDerivedEquipment(inst)) continue;
        delete state.instances[id];
        state.instanceOrder = state.instanceOrder.filter((other) => other !== id);
      }
      for (const id of [...state.templateOrder]) {
        if (state.templates[id]?.metadata?.source !== 'auto-extraction') continue;
        delete state.templates[id];
        state.templateOrder = state.templateOrder.filter((other) => other !== id);
      }
      if (state.selectedInstanceId && !state.instances[state.selectedInstanceId]) {
        state.selectedInstanceId = null;
      }
      state.selectedInstanceIds = state.selectedInstanceIds.filter((id) =>
        Boolean(state.instances[id])
      );
      if (!state.selectedInstanceId) {
        state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      }
      if (state.portEditInstanceId && !state.instances[state.portEditInstanceId]) {
        state.portEditInstanceId = null;
        state.selectedPortId = null;
      }
      state.layoutSaveMarkedIds = state.layoutSaveMarkedIds.filter((id) =>
        Boolean(state.instances[id])
      );
      state.reviewSelectedIds = [];
      if (action.payload?.clearReviewDecisions) state.reviewDecisions = {};
      state.dxfOverlaysGenerated = action.payload?.keepGenerated ?? false;
    },

    // ── Undo / redo (equipment-local) ─────────────────────────────────────
    undoEquipment: (state) => {
      const prev = state.history.past.pop();
      if (!prev) return;
      // The redo entry describes the same edit, so it keeps that edit's timestamp.
      state.history.future.push({ ...snapshot(state), timestamp: prev.timestamp });
      state.templates = prev.templates;
      state.templateOrder = prev.templateOrder;
      state.instances = prev.instances;
      state.instanceOrder = prev.instanceOrder;
      state.reviewDecisions = prev.reviewDecisions;
      state.reviewSelectedIds = [];
      if (state.selectedInstanceId && !state.instances[state.selectedInstanceId]) {
        state.selectedInstanceId = null;
      }
      state.selectedInstanceIds = state.selectedInstanceIds.filter((id) =>
        Boolean(state.instances[id])
      );
      if (!state.selectedInstanceId) {
        state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      }
    },
    redoEquipment: (state) => {
      const next = state.history.future.pop();
      if (!next) return;
      state.history.past.push({ ...snapshot(state), timestamp: next.timestamp });
      state.templates = next.templates;
      state.templateOrder = next.templateOrder;
      state.instances = next.instances;
      state.instanceOrder = next.instanceOrder;
      state.reviewDecisions = next.reviewDecisions;
      state.reviewSelectedIds = [];
      if (state.selectedInstanceId && !state.instances[state.selectedInstanceId]) {
        state.selectedInstanceId = null;
      }
      state.selectedInstanceIds = state.selectedInstanceIds.filter((id) =>
        Boolean(state.instances[id])
      );
      if (!state.selectedInstanceId) {
        state.selectedInstanceId = state.selectedInstanceIds[0] ?? null;
      }
    },
    /**
     * Drop the equipment redo stack. Dispatched when a *layout* edit is recorded:
     * undo/redo is global, so any new edit invalidates pending redos everywhere.
     */
    clearEquipmentRedo: (state) => {
      state.history.future = [];
    },
  },
});

export const {
  setEquipmentVisible,
  toggleEquipmentVisible,
  setDxfEquipmentOverlays,
  clearDxfEquipmentOverlays,
  setShowLabels,
  toggleShowLabels,
  setShowPorts,
  toggleShowPorts,
  toggleProcessVisible,
  setProcessVisible,
  setAllProcessesVisible,
  selectInstance,
  selectInstances,
  selectTemplate,
  setPortEditInstance,
  selectEquipmentPort,
  toggleLayoutSaveMark,
  addLayoutSaveMark,
  addLayoutSaveMarks,
  setLayoutSaveMarks,
  clearLayoutSaveMarks,
  setEquipmentReviewSelection,
  toggleEquipmentReviewSelection,
  setSelectedEquipmentReviewStatus,
  resetDxfEquipmentReview,
  setDrawActive,
  setDrawMode,
  addDrawPoint,
  popDrawPoint,
  clearDrawPoints,
  setPendingPolygon,
  clearPending,
  beginEditInstance,
  addInstance,
  updateInstance,
  moveInstance,
  translateInstances,
  recordEquipmentHistory,
  setInstancePolygon,
  removeInstance,
  removeInstances,
  duplicateInstance,
  addPort,
  updatePort,
  removePort,
  addTemplate,
  updateTemplate,
  renameTemplate,
  duplicateTemplate,
  removeTemplate,
  loadEquipment,
  clearEquipment,
  undoEquipment,
  redoEquipment,
  clearEquipmentRedo,
} = equipmentSlice.actions;

// ── Factory helpers ──────────────────────────────────────────────────────────

/** Build a new instance from a footprint + form fields (+ optional template). */
export function makeInstance(params: {
  polygon: EquipmentPoint[];
  rotation: number;
  name: string;
  objectType: EquipmentInstance['objectType'];
  category: EquipmentInstance['category'];
  equipmentType?: EquipmentProcessType;
  description?: string;
  tags?: string[];
  notes?: string;
  templateId?: string | null;
  overrides?: EquipmentInstance['overrides'];
  foupCapacity?: number;
  outsideStocker?: boolean;
  processWaitSec?: number;
  /** Existing layout + equipment ids for uniqueness (esp. shared ST). */
  existingIds?: string[];
}): EquipmentInstance {
  const iso = new Date().toISOString();
  const isStocker = params.objectType === 'stocker';
  const usesProcessWait = isStocker || params.objectType === 'equipment';
  const alloc = createEntityIdAllocator(params.existingIds ?? []);
  return {
    id: alloc.nextForEquipment(params.objectType),
    templateId: params.templateId ?? null,
    name: params.name,
    objectType: params.objectType,
    category: params.category,
    equipmentType: params.equipmentType ?? categoryToProcessType(params.category),
    description: params.description ?? '',
    tags: params.tags ?? [],
    notes: params.notes ?? '',
    polygon: params.polygon,
    rotation: params.rotation,
    overrides: params.overrides ?? {},
    foupCapacity: isStocker
      ? (params.foupCapacity ?? DEFAULT_FOUP_CAPACITY)
      : params.objectType === 'equipment'
        ? (params.foupCapacity ?? DEFAULT_TOOL_FOUP_CAPACITY)
        : params.foupCapacity,
    outsideStocker: isStocker ? Boolean(params.outsideStocker) : params.outsideStocker,
    // Untyped until the user picks process type(s); do not invent PHOTO for stockers.
    ...(isStocker ? { processTypes: [] as const } : {}),
    processWaitSec: usesProcessWait
      ? (params.processWaitSec ??
        (isStocker ? DEFAULT_STOCKER_PROCESS_WAIT_SEC : DEFAULT_PROCESS_WAIT_SEC))
      : params.processWaitSec,
    metadata: {},
    createdAt: iso,
    updatedAt: iso,
  };
}

/** Build a reusable template from an existing instance ("Save as Template"). */
export function makeTemplateFromInstance(
  instance: EquipmentInstance,
  templates: Record<string, EquipmentTemplate>,
  name: string
): EquipmentTemplate {
  const iso = new Date().toISOString();
  const existingTemplate = instance.templateId ? templates[instance.templateId] : null;
  const style = {
    ...(existingTemplate?.defaultStyle ?? defaultStyleForType(instance.objectType)),
    ...(instance.overrides.style ?? {}),
  };
  const ports = instance.overrides.ports ?? existingTemplate?.ports ?? [];
  const properties = {
    ...(existingTemplate?.defaultProperties ?? {}),
    ...(instance.overrides.properties ?? {}),
  };
  return {
    id: generateId(),
    name,
    objectType: instance.objectType,
    category: instance.category,
    icon: style.icon,
    defaultStyle: style,
    ports: JSON.parse(JSON.stringify(ports)),
    defaultProperties: properties,
    metadata: { source: 'instance', version: EQUIPMENT_LAYER_VERSION },
    createdAt: iso,
    updatedAt: iso,
  };
}

export default equipmentSlice.reducer;
